import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import MonthlyBarChart from './MonthlyBarChart';

/**
 * How good the identification model is (GET /ai/metrics).
 *
 * Two kinds of evidence, kept apart on purpose:
 *   - the frozen-test evaluation recorded when a model was trained -- the
 *     numbers the promotion gate compares (top-1, macro-F1, ECE);
 *   - live agreement from BLIND expert feedback: answers given before the
 *     expert saw the AI's. Answers given after are left out; they measure how
 *     persuasive the page was, not whether the model was right.
 * Per-genus precision/recall and the commonest confusions are the confusion
 * matrix read the useful way round: where the model goes wrong, and with what.
 */
// Whatever arrives, the page renders: missing lists are empty lists.
export function normalise(body) {
  const b = body || {};
  const genus = b.genus || {};
  return {
    evaluations: Array.isArray(b.evaluations) ? b.evaluations : [],
    by_model: Array.isArray(b.by_model) ? b.by_model : [],
    by_month: Array.isArray(b.by_month) ? b.by_month : [],
    genus: { n: genus.n || 0, genera: Array.isArray(genus.genera) ? genus.genera : [],
             confusions: Array.isArray(genus.confusions) ? genus.confusions : [] },
  };
}

/**
 * The production model's evaluation to headline: its frozen-test one (the
 * gate's) when it has one, else its measurement on the held-out reference
 * split (migration 0068) -- the only numbers the served model has until it is
 * evaluated on a frozen version. A production model with neither still names
 * itself, with blank figures.
 */
export function productionEvaluation(evaluations) {
  const mine = (evaluations || []).filter((e) => e.stage === 'production');
  return mine.find((e) => (e.source || 'frozen_test') === 'frozen_test' && e.top1 != null)
    || mine.find((e) => e.top1 != null) || mine[0];
}

const pct = (v) => (v == null ? '—' : `${Math.round(v * 1000) / 10}%`);
const num = (v, digits = 3) => (v == null ? '—' : Number(v).toFixed(digits));

export default function AiModelDashboard({ compact = false }) {
  const { t } = useTranslation('ai');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    api.get('/ai/metrics')
      .then(({ data: body }) => { if (live) setData(normalise(body)); })
      .catch((e) => { if (live) setError(e?.response?.data?.error || e?.message || String(e)); });
    return () => { live = false; };
  }, []);

  if (error) return <p role="alert" data-testid="ai-metrics-error">{t('metrics.loadFailed', { error })}</p>;
  if (!data) return <p>{t('metrics.loading')}</p>;

  const production = productionEvaluation(data.evaluations);
  const reference = production?.source === 'reference_holdout';
  const months = data.by_month.filter((m) => m.agree_rate != null)
    .map((m) => ({ month: m.month, count: Math.round(m.agree_rate * 100) }));
  const genera = compact ? data.genus.genera.slice(0, 8) : data.genus.genera.slice(0, 25);

  return (
    <section data-testid="ai-metrics" style={{ display: 'grid', gap: 14 }}>
      <h3 style={{ margin: 0 }}>{t('metrics.title')}</h3>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--c-text-muted-fg)' }}>{t('metrics.intro')}</p>

      <div style={grid} data-testid="ai-metrics-production">
        <Stat label={t('metrics.productionModel')} value={production?.model_version || t('metrics.none')}
              tip={t('metrics.tip.production')} />
        <Stat label={t('metrics.top1')} value={pct(production?.top1)} tip={t('metrics.tip.top1')} />
        {reference
          ? <Stat label={t('metrics.top5')} value={pct(production?.top5)} tip={t('metrics.tip.top5')} />
          : <Stat label={t('metrics.macroF1')} value={num(production?.macro_f1)} tip={t('metrics.tip.macroF1')} />}
        <Stat label={t('metrics.ece')} value={num(production?.ece)} tip={t('metrics.tip.ece')} />
        <Stat label={t('metrics.testN')} value={production?.n ?? '—'} tip={t('metrics.tip.testN')} />
      </div>
      {reference && (
        <p style={muted} data-testid="ai-metrics-reference-note">{t('metrics.referenceHoldout', { n: production.n })}</p>
      )}

      <div>
        <h4 style={h4}>{t('metrics.liveHeading')}</h4>
        <p style={muted}>{t('metrics.liveIntro', { n: data.genus.n })}</p>
        <MonthlyBarChart data={months} emptyMessage={t('metrics.noFeedback')} />
      </div>

      {!compact && (
        <div>
          <h4 style={h4}>{t('metrics.versionsHeading')}</h4>
          <table style={table} data-testid="ai-metrics-versions">
            <thead><tr>
              <th>{t('metrics.version')}</th><th>{t('metrics.stage')}</th><th>{t('metrics.top1')}</th>
              <th>{t('metrics.top5')}</th><th>{t('metrics.macroF1')}</th><th>{t('metrics.ece')}</th>
              <th>{t('metrics.testN')}</th><th>{t('metrics.source')}</th><th>{t('metrics.liveAgree')}</th>
            </tr></thead>
            <tbody>
              {data.evaluations.map((e) => {
                const live = data.by_model.find((m) => m.model_version === e.model_version);
                return (
                  <tr key={`${e.model_version}-${e.source || ''}-${e.evaluated_at}`}>
                    <td dir="ltr">{e.model_version}</td><td>{t(`metrics.stages.${e.stage}`, { defaultValue: e.stage })}</td>
                    <td>{pct(e.top1)}</td><td>{pct(e.top5)}</td><td>{num(e.macro_f1)}</td><td>{num(e.ece)}</td>
                    <td>{e.n ?? '—'}</td>
                    <td>{e.source ? t(`metrics.sources.${e.source}`, { defaultValue: e.source }) : '—'}</td>
                    <td>{live ? `${pct(live.agree_rate)} (n=${live.blind_total})` : '—'}</td>
                  </tr>
                );
              })}
              {!data.evaluations.length && <tr><td colSpan={9}>{t('metrics.noModels')}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <div>
        <h4 style={h4}>{t('metrics.genusHeading')}</h4>
        {genera.length ? (
          <table style={table} data-testid="ai-metrics-genera">
            <thead><tr>
              <th>{t('metrics.genus')}</th><th title={t('metrics.tip.support')}>{t('metrics.support')}</th>
              <th title={t('metrics.tip.precision')}>{t('metrics.precision')}</th>
              <th title={t('metrics.tip.recall')}>{t('metrics.recall')}</th>
            </tr></thead>
            <tbody>
              {genera.map((g) => (
                <tr key={g.genus}>
                  <td><em>{g.genus}</em></td><td>{g.support}</td><td>{pct(g.precision)}</td><td>{pct(g.recall)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p style={muted}>{t('metrics.noFeedback')}</p>}
      </div>

      <div>
        <h4 style={h4}>{t('metrics.confusionsHeading')}</h4>
        {data.genus.confusions.length ? (
          <ol style={{ margin: 0, paddingInlineStart: 20 }} data-testid="ai-metrics-confusions">
            {data.genus.confusions.map((c) => (
              <li key={`${c.true}-${c.predicted}`}>
                {t('metrics.confusion', { truth: c.true, predicted: c.predicted, count: c.count })}
              </li>
            ))}
          </ol>
        ) : <p style={muted}>{t('metrics.noConfusions')}</p>}
      </div>
    </section>
  );
}

function Stat({ label, value, tip }) {
  return (
    <div style={stat} title={tip}>
      <div style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600 }} dir="ltr">{value}</div>
    </div>
  );
}

const grid = { display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' };
const stat = { border: '1px solid var(--c-border)', borderRadius: 8, padding: '8px 10px', background: 'var(--c-surface)' };
const h4 = { margin: '0 0 6px' };
const muted = { margin: '0 0 6px', fontSize: 13, color: 'var(--c-text-muted-fg)' };
const table = { borderCollapse: 'collapse', width: '100%', fontSize: 13 };
